import React, { useState, useMemo, useCallback } from 'react'
import { useSelector } from '../../../store'
import {
  ContainerPricingModel,
  Product,
} from '../../../modules/productsListing/types'
import {
  EulaType,
  FieldMetadata,
  FieldMetadataField,
} from '../../../modules/privateOffer/types'
import { ProductNameSection } from '../../../common/components/ProductNameSection/ProductNameSection'
import { PrivateOfferDetailsSection } from './PrivateOfferDetailsSection/PrivateOfferDetailsSection'
import { useStyles } from './PrivateOfferFormContainer.styles'
import { AdditionalPaymentConfiguration } from './AdditionalPaymentConfiguration/AdditionalPaymentConfiguration'
import { PrivateOfferContractDuration } from './PrivateOfferContractDuration/PrivateOfferContractDuration'
import {
  PlanType,
  RadioButtonGroupMapping,
  RadioButtonType,
} from '@labrav/react-components'
import { AdditionalUsageDimensionsV2 } from '../../../common/components/AdditionalUsageDimensionsV2/AdditionalUsageDimensionsV2'
import { Dimension as DimensionV2 } from '../../Subscription/RenewOfferSubscription/Dimension/Dimension'
import { EulaSection } from '../../../common/components/EulaSection/EulaSection'
import { SubscriptionSectionWrapper } from '../../Subscription/SubscriptionSectionWrapper/SubscriptionSectionWrapper'
import { PrivateOfferRenewalPricingTerms } from '../../../common/components/PrivateOfferRenewalPricingTerms/PrivateOfferRenewalPricingTerms'
import { PrivateOfferRenewalInstallments } from '../../../common/components/PrivateOfferRenewalInstallments/PrivateOfferRenewalInstallments'
import { PrivateOfferInstallments } from './PrivateOfferInstallments/PrivateOfferInstallments'
import { PrivateOfferCustomMetaData } from './PrivateOfferCustomeMetaData/PrivateOfferCustomMetaData'
import { PrivateOfferRenewalSection } from './PrivateOfferRenewalSection/PrivateOfferRenewalSection'
import { MainTabsV2 } from '@labrav/react-components'
import { containerProductHourlyAndUsage } from '../../../modules/privateOffer/types'
import { FormikProps, useFormikContext } from 'formik'
import { scrollspyTabs, ScrollToFieldError } from '@labrav/common'
import { useTheme } from '@mui/material'
import { PrivateOfferProductDimensions } from './PrivateOfferProductDimensions/PrivateOfferProductDimensions'
import { PrivateOfferContractPricingTerm } from './PrivateOfferContractPricingTerm/PrivateOfferContractPricingTerm'
import { UsageDuration } from './UsageDuration/UsageDuration'
import { PrivateOfferUsageDimensions } from './PrivateOfferUsageDimensions/PrivateOfferUsageDimensions'
import { defaultNow } from '../../../common/utils/dateTimeNow'
import { eulaStandardContractUrl } from '../../../common/utils/constants'
import { PrivateOfferStakeholders } from './PrivateOfferStakeholders/PrivateOfferStakeholders'
import { getIsErrorForOfferTab } from './getIsErrorForOfferTab'
import { OffersWithErrors } from '../../../modules/privateOffer/privateOfferCreation/types'
import { faShelves } from '@fortawesome/pro-regular-svg-icons'
import { useFlagValue } from '@labrav/flags'
import { PricingTool } from './PricingTool/PricingTool'
import { PrivateOfferInstances } from './PrivateOfferInstances/PrivateOfferInstances'
import { AmiPrivateOfferInstallmentsSection } from './AmiPrivateOfferInstallmentsSection/AmiPrivateOfferInstallmentsSection'
import { FlyOutListingData } from '../../../types/flyout'
import { PrivateOfferAmiDimensions } from './PrivateOfferAmiDimensions/PrivateOfferAmiDimensions'
import { MarketplaceTemplateDataResponse } from '../../../types/mappingManager'
import type { StakeholderUserOption } from '../../../common/components/ViewStakeholders/stakeholderUserOption'
import {
  MicrosoftFormFAQs,
  MicrosoftFormFAQsItem,
} from '../AzureOffer/MicrosoftFormFAQs/MicrosoftFormFAQs'

export type PrivateOfferFormContainerProps = {
  product: Product
  offset: number
  hideStakeholders?: boolean
  tabTop?: string
  stakeholderUsers?: StakeholderUserOption[]
}

const EulaFaqs = () => <MicrosoftFormFAQs item={MicrosoftFormFAQsItem.EULA} />
const StakeholdersFaqs = () => (
  <MicrosoftFormFAQs item={MicrosoftFormFAQsItem.STAKEHOLDERS} />
)

function PrivateOfferFormContainerComponent({
  product,
  offset,
  hideStakeholders,
  tabTop,
  stakeholderUsers = [],
}: PrivateOfferFormContainerProps) {
  const { classes } = useStyles({ offset })
  const theme = useTheme()
  const formikValues = useFormikContext() as FormikProps<OffersWithErrors>
  const { values, errors } = formikValues
  const [eulaErr, setEulaErr] = useState<string>('')
  const templateData = useSelector<MarketplaceTemplateDataResponse | undefined>(
    state => state.marketplaceMappingManager?.templateData?.templateData
  )
  const fieldMetadata = useSelector<FieldMetadata | undefined>(
    state =>
      state.privateOffersInCreation.currentPrivateOfferInCreation[
        product.productId
      ]?.fieldMetadata
  )

  const fieldMetadataMetaData =
    templateData?.fieldMappings?.fields?.fieldsMapping?.Labra__Metadata_Hide__c
      ?.isHidden
  const fieldMetadataStakeholders =
    templateData?.fieldMappings?.fields?.fieldsMapping?.Labra__Stakeholders__c
      ?.isHidden

  const flyoutSellerInfo = useSelector<FlyOutListingData>(
    state => state.flyoutSellerInfo
  )
  const isIndiaRegistered = flyoutSellerInfo?.countryRegistered === 'IN'

  const noteDescription = useMemo(
    () =>
      isIndiaRegistered
        ? 'As this account is based in India, you can use either of USD or INR currency. '
        : '',
    [isIndiaRegistered]
  )

  const { value: isMappingManagerUiEnabled } = useFlagValue('mappingManager')
  const { value: isRenewalPricingTermEnabled } =
    useFlagValue('renewalPricingTerm')
  const isEulaFileReadOnly = (fieldMetadata?.eulaFile as FieldMetadataField)
    ?.readOnly
  const isEulaFileHidden = (fieldMetadata?.eulaFile as FieldMetadataField)
    ?.hidden
  const isEulaTypeReadOnly = (fieldMetadata?.eulaType as FieldMetadataField)
    ?.readOnly
  const isEulaTypeHidden = (fieldMetadata?.eulaType as FieldMetadataField)
    ?.hidden

  const isAgreementStartDateHidden = (
    fieldMetadata?.agreementStartDate as FieldMetadataField
  )?.hidden

  const isContractDurationHidden = (
    fieldMetadata?.serviceLength as FieldMetadataField
  )?.hidden

  const isContractDurationSectionHidden =
    isContractDurationHidden && isAgreementStartDateHidden

  const shouldRenderEulaSection = useMemo(
    () => !isMappingManagerUiEnabled || !(isEulaFileHidden && isEulaTypeHidden),
    [isMappingManagerUiEnabled, isEulaFileHidden, isEulaTypeHidden]
  )

  const isUsageOrHourlyPricingModal = useMemo(
    () =>
      product?.containerPricingModel === ContainerPricingModel.HOURLY ||
      product?.containerPricingModel === ContainerPricingModel.USAGE,
    [product?.containerPricingModel]
  )

  const eulaButtonOptionsV2 = useMemo<RadioButtonGroupMapping[]>(
    () => [
      {
        key: EulaType.PUBLIC,
        label: 'Public offer legal terms',
        type: RadioButtonType.STANDARD,
        url: product?.eulaUrl || '',
      },
      {
        key: EulaType.STANDARD,
        label: 'Standard contract for AWS marketplace (SCMP)',
        type: RadioButtonType.STANDARD,
        infoDescription:
          'SCMP is a standardized contract template intended to help streamline procurement and speed up transactions.',
        url: eulaStandardContractUrl,
      },
      {
        key: EulaType.CUSTOM,
        label: 'Custom EULA',
        type: RadioButtonType.STANDARD,
        infoDescription:
          'Provide a URL to a custom end-user license agreement (EULA) for your product.',
      },
    ],
    [product?.eulaUrl]
  )

  const updateErr = useCallback((error: string) => {
    setEulaErr(error)
  }, [])

  const additionalUsageObjects = useMemo(
    () =>
      (values.dimensions || []).filter(
        ({ isAdditionalUsageAllowed }) => isAdditionalUsageAllowed
      ),
    [values.dimensions]
  )

  const offerDetailsNoteDescription = useMemo(
    () =>
      isIndiaRegistered
        ? "As this account is based in India, you can only target buyers based in India. Buyers outside India won't be able to accept offers that you create"
        : '',
    [isIndiaRegistered]
  )

  const ContainerOfferTabs = useMemo(
    () => [
      {
        key: 'offer-details',
        label: 'Offer details',
        renderElement: (
          <div id={'offer-details'} className={classes.scrollTopMargin}>
            <PrivateOfferDetailsSection
              product={product}
              noteDescription={offerDetailsNoteDescription}
            />
            {isRenewalPricingTermEnabled && (
              <PrivateOfferRenewalSection product={product} />
            )}
          </div>
        ),
        isError: getIsErrorForOfferTab(
          'offer-details',
          formikValues,
          eulaErr,
          product?.productType,
          !!isRenewalPricingTermEnabled
        ),
      },
      {
        key: 'offer-pricing',
        label: 'Pricing',
        renderElement: (
          <div id={'offer-pricing'} className={classes.scrollTopMargin}>
            <AdditionalPaymentConfiguration
              product={product}
              noteDescription={noteDescription}
            />

            {(product.productType === PlanType.SAAS_CONTRACT ||
              product.productType === PlanType.CONTAINER) &&
              !isContractDurationSectionHidden && (
                <>
                  <PrivateOfferContractDuration product={product} />
                </>
              )}
            {product.productType === PlanType.AMI && (
              <>
                {product.containerPricingModel ===
                ContainerPricingModel.CONTRACT ? (
                  <>
                    <PrivateOfferAmiDimensions product={product} />
                  </>
                ) : (
                  <>
                    <PricingTool product={product} />
                    <PrivateOfferInstances product={product} />
                  </>
                )}

                {values?.flexiblePaymentTerms && (
                  <AmiPrivateOfferInstallmentsSection />
                )}
              </>
            )}
            {values.containerOfferServiceLength !== '1H' &&
              product.productType === PlanType.CONTAINER && (
                <>
                  {product.containerPricingModel ===
                    ContainerPricingModel.CONTRACT ||
                  (product.containerPricingModel ===
                    ContainerPricingModel.USAGE &&
                    values.flexiblePaymentTerms === true) ? (
                    <PrivateOfferProductDimensions product={product} />
                  ) : (
                    <PrivateOfferContractPricingTerm product={product} />
                  )}
                </>
              )}

            {product.productType === PlanType.CONTAINER &&
              product.containerPricingModel &&
              containerProductHourlyAndUsage.includes(
                product.containerPricingModel
              ) &&
              !values?.flexiblePaymentTerms && <UsageDuration />}

            {isUsageOrHourlyPricingModal &&
              product.productType === PlanType.CONTAINER && (
                <PrivateOfferUsageDimensions product={product} />
              )}

            {product.productType === PlanType.CONTAINER &&
              values.flexiblePaymentTerms && (
                <PrivateOfferInstallments product={product} />
              )}

            {(product.productType === PlanType.SAAS_CONTRACT ||
              product.productType === PlanType.SAAS_SUBSCRIPTION) && (
              <>
                <>
                  <SubscriptionSectionWrapper
                    children={
                      <DimensionV2
                        productId={product.productId}
                        product={product}
                        entitlementOption={values.entitlementOption}
                      />
                    }
                    heading="Pricing configuration"
                    icon={faShelves}
                    // Temporarily disabled HelpDrawer for Pricing configuration until content is finalized
                    // secondaryActions={
                    //   <HelpDrawer
                    //     headerTitle="Pricing configuration"
                    //     sectionName="combinedFormPlans"
                    //   />
                    // }
                  />
                </>
                {values.flexiblePaymentTerms && (
                  <PrivateOfferInstallments product={product} />
                )}
                {product.productType === PlanType.SAAS_CONTRACT &&
                additionalUsageObjects.length > 0 ? (
                  <SubscriptionSectionWrapper
                    children={
                      <AdditionalUsageDimensionsV2
                        formik={
                          formikValues as unknown as FormikProps<
                            Record<string, unknown>
                          >
                        }
                      />
                    }
                    heading="Usage dimensions"
                    icon={faShelves}
                  />
                ) : (
                  <></>
                )}
              </>
            )}
          </div>
        ),
        isError: getIsErrorForOfferTab(
          'offer-pricing',
          formikValues,
          eulaErr,
          product?.productType
        ),
      },
      {
        key: 'offer-other',
        label: 'Other',
        renderElement: (
          <div id={'offer-other'} className={classes.scrollTopMargin}>
            {!isRenewalPricingTermEnabled && (
              <PrivateOfferRenewalSection product={product} />
            )}
            {isRenewalPricingTermEnabled &&
              product.productType === PlanType.SAAS_CONTRACT && (
                <>
                  <PrivateOfferRenewalPricingTerms />
                  <PrivateOfferRenewalInstallments />
                </>
              )}
            {shouldRenderEulaSection && (
              <EulaSection
                defaultNow={defaultNow}
                formik={
                  formikValues as unknown as FormikProps<
                    Record<string, unknown>
                  >
                }
                isMultiFileUpload={true}
                fileUploadDescription={
                  'Upload up to 5 files one by one, use plain text .txt or PDF formats only. The maximum file size is 10 MB.'
                }
                fileUploadButtonText={'Upload files'}
                fileUploadContainerTitle={'Drag and drop files here'}
                quantity={5}
                eulaOptionsHeading={'Legal term documents'}
                isOptional={values?.eulaType !== EulaType.CUSTOM}
                eulaOptionSubHeading={
                  values?.eulaType === EulaType.STANDARD
                    ? 'Upload up to 5 files one by one to amend the Standard contract for AWS marketplace, use plain text .txt or PDF formats only. The maximum file size is 10 MB. Once uploaded, all files will be combined into a single PDF. The filed will be presented to buyers in the order they are uploaded.'
                    : 'Upload up to 5 files one by one using plain text (.txt) or PDF formats only. The maximum file size is 10 MB. Once uploaded, all files will be combined into a single PDF. The files will be presented to buyers in the order they are uploaded'
                }
                eulaOptions={eulaButtonOptionsV2}
                fileSize={10}
                updateErr={updateErr}
                uploadError={
                  eulaErr ||
                  (formikValues?.touched.eulaFile
                    ? formikValues?.errors?.eulaFile
                    : '')
                }
                disableEulaFileUpload={isEulaFileReadOnly}
                showEulaFileUpload={isEulaFileHidden ? false : true}
                disableEulaOptions={isEulaTypeReadOnly}
                showEulaOptions={isEulaTypeHidden ? false : true}
                secondaryActions={<EulaFaqs />}
              />
            )}

            {!fieldMetadataStakeholders && (
              <PrivateOfferStakeholders
                hideStakeholders={hideStakeholders}
                users={stakeholderUsers}
                stakeholdersFaqs={<StakeholdersFaqs />}
              />
            )}
            {!fieldMetadataMetaData && (
              <PrivateOfferCustomMetaData product={product} />
            )}
          </div>
        ),
        isError: getIsErrorForOfferTab(
          'offer-other',
          formikValues,
          eulaErr,
          product?.productType,
          !!isRenewalPricingTermEnabled
        ),
      },
    ],
    [
      product,
      formikValues,
      eulaErr,
      classes.scrollTopMargin,
      noteDescription,
      offerDetailsNoteDescription,
      shouldRenderEulaSection,
      isUsageOrHourlyPricingModal,
      eulaButtonOptionsV2,
      updateErr,
      additionalUsageObjects,
      hideStakeholders,
      isEulaFileReadOnly,
      isEulaFileHidden,
      isEulaTypeReadOnly,
      isEulaTypeHidden,
      stakeholderUsers,
      fieldMetadataStakeholders,
      fieldMetadataMetaData,
      isContractDurationSectionHidden,
      isRenewalPricingTermEnabled,
    ]
  )

  const { activeTabInex, scrollToSection } = scrollspyTabs({
    tabs: ContainerOfferTabs,
    offset: offset,
  })

  const customErrorFields = useMemo(
    () => (errors ? Object.keys(errors) : []),
    [errors]
  )

  return (
    <div className={classes.root}>
      <ScrollToFieldError customErrorFields={customErrorFields} />
      <ProductNameSection product={product} />
      <MainTabsV2
        activeTabIndex={activeTabInex}
        onChange={scrollToSection}
        tabs={ContainerOfferTabs}
        backGroundColor={theme.palette.textBackground.light}
        tabPosition="sticky"
        tabTop={tabTop}
      />
      {ContainerOfferTabs.map(
        containerOfferTabItem => containerOfferTabItem.renderElement
      )}
    </div>
  )
}

export const PrivateOfferFormContainer = React.memo(
  PrivateOfferFormContainerComponent
)
